import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Navigation.css';

const Navigation = ({ activeTab, onTabChange, user, isOpen, onClose, isCollapsed, onToggleCollapsed }) => {
  const { logout, isAdmin, impersonationData, stopImpersonation } = useAuth();
  
  // Base navigation items for all users
  const baseNavItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: '📊'
    },
    {
      id: 'upload',
      label: 'Upload Document',
      icon: '📤'
    },
    {
      id: 'reviews',
      label: 'Document Reviews',
      icon: '📋'
    },
    {
      id: 'organization',
      label: 'Organization Tree',
      icon: '🏛️'
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: '💬'
    }
  ];
  
  // Add admin item if user is admin
  const navItems = isAdmin() ? [
    ...baseNavItems,
    {
      id: 'admin',
      label: 'Admin Panel',
      icon: '⚙️'
    }
  ] : baseNavItems;

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    // Close mobile menu when item is selected
    if (onClose) {
      onClose();
    }
  };

  const handleStopImpersonation = async () => {
    if (window.confirm('Are you sure you want to stop impersonating and return to your admin account?')) {
      try {
        const result = await stopImpersonation();
        if (result.success) {
          alert('Stopped impersonation. You are now back to your admin account.');
        } else {
          alert('Failed to stop impersonation: ' + result.error);
        }
      } catch (error) {
        alert('Failed to stop impersonation: ' + error.message);
      }
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="nav-overlay" onClick={onClose}></div>}
      
      <nav className={`navigation ${isOpen ? 'mobile-open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="nav-header">
          <div className="nav-header-content">
            {!isCollapsed && <h3>Navigation</h3>}
            {/* Mobile close button */}
            {onClose && (
              <button 
                className="nav-close-btn mobile-only" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                aria-label="Close navigation"
              >
                ×
              </button>
            )}
            {/* Desktop collapse/expand button */}
            {onToggleCollapsed && (
              <button 
                className="nav-toggle-btn desktop-only" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleCollapsed();
                }}
                aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
                title={isCollapsed ? "Expand navigation" : "Collapse navigation"}
              >
                {isCollapsed ? '›' : '‹'}
              </button>
            )}
          </div>
        {!isCollapsed && (
          <div className="user-badge">
            <div className="user-avatar">
              {(user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-name">
                {user?.first_name ? `${user.first_name} ${user?.last_name || ''}`.trim() : user?.username}
              </div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
        )}
        {isCollapsed && (
          <div className="user-avatar-collapsed">
            {(user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase()}
          </div>
        )}
        
        {/* Impersonation indicator */}
        {impersonationData?.isImpersonating && (
          <div className="impersonation-indicator">
            {!isCollapsed && (
              <>
                <div className="impersonation-warning">
                  ⚠️ Impersonating {impersonationData.targetUser.username}
                </div>
                <div className="impersonation-admin">
                  Admin: {impersonationData.adminUser.username}
                </div>
                <button 
                  className="stop-impersonation-btn"
                  onClick={handleStopImpersonation}
                  title="Stop impersonating and return to admin account"
                >
                  Stop Impersonating
                </button>
              </>
            )}
            {isCollapsed && (
              <div 
                className="impersonation-indicator-collapsed"
                title={`Impersonating ${impersonationData.targetUser.username} (Admin: ${impersonationData.adminUser.username})`}
                onClick={handleStopImpersonation}
              >
                ⚠️
              </div>
            )}
          </div>
        )}
      </div>
      <ul className="nav-list">
        {navItems.map((item) => (
          <li key={item.id} className="nav-item">
            <button
              className={`nav-button ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => handleTabChange(item.id)}
              title={isCollapsed ? item.label : undefined}
            >
              <span className="nav-icon">{item.icon}</span>
              {!isCollapsed && <span className="nav-label">{item.label}</span>}
            </button>
          </li>
        ))}
      </ul>

      <div className="nav-footer">
        <button 
          className="logout-button" 
          onClick={handleLogout}
          title={isCollapsed ? "Logout" : undefined}
        >
          <span className="nav-icon">🚪</span>
          {!isCollapsed && <span className="nav-label">Logout</span>}
        </button>
      </div>
    </nav>
    </>
  );
};

export default Navigation;